/**
 * Anant Portfolio Tracker System (APTS) — main user page
 * Tabs: Dashboard · Ledger · Assets/Liab · Goals · Plans
 * Uses existing SSO (JWT) + AT Wallet subscription flow
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Wallet, Target, Plus, X, Trash2, Calendar, PieChart, Award, AlertTriangle, DollarSign, IndianRupee } from 'lucide-react';
import api from '../../utils/api';
import ATWalletBadge from '../../components/ATWalletBadge';
import ShareButton from '../../components/ShareButton';

const CATS_INCOME = ['salary', 'business', 'freelance', 'rental', 'interest', 'dividend', 'gift', 'other'];
const CATS_EXPENSE = ['food', 'rent', 'utilities', 'transport', 'groceries', 'medical', 'education', 'entertainment', 'shopping', 'emi', 'other'];
const ITEM_CATS_ASSET = ['savings', 'fd', 'mutual_fund', 'stocks', 'gold', 'property', 'vehicle', 'other'];
const ITEM_CATS_LIAB = ['home_loan', 'car_loan', 'personal_loan', 'credit_card', 'business_loan', 'other'];

export default function APTS() {
  const [tab, setTab] = useState('dashboard');
  const [subscription, setSubscription] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [remainingDays, setRemainingDays] = useState(0);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadSub = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([
        api.get('/apts/me/subscription'),
        api.get('/apts/plans'),
      ]);
      setSubscription(s.data.subscription);
      setHasAccess(s.data.has_access);
      setRemainingDays(s.data.remaining_days);
      setPlans(p.data.plans || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSub(); }, [loadSub]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white">Loading…</div>;

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-950 text-white pb-16">
      <div className="max-w-6xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Wallet className="w-7 h-7 text-indigo-400" />
              <h1 className="text-2xl sm:text-3xl font-extrabold">Anant Portfolio Tracker</h1>
            </div>
            <div className="text-xs text-slate-400 mt-0.5">Personal finance · Net worth · Ledger · Goals · EMI alerts</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <ShareButton type="page" itemName="Anant Portfolio Tracker" variant="icon" className="scale-110" />
              <a
                href={(() => {
                  const link = `${window.location.origin}/apts`;
                  const msg = `📊 Anant Portfolio Tracker try karo!\n\nPersonal finance · Net worth · Khata Bahi (Paise Liye/Diye) · EMI alerts · Goals — sab kuch ek jagah.\nFree 7-day trial se shuru karo.\n\n${link}`;
                  return `https://wa.me/?text=${encodeURIComponent(msg)}`;
                })()}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-600 text-white hover:scale-105 transition shadow-lg shadow-green-500/30"
                data-testid="apts-viral-share-wa"
                title="Share on WhatsApp"
              >
                ✨ Share & Earn
              </a>
            </div>
            {subscription && (
              <>
                <ATWalletBadge compact />
                <div className={`rounded-xl px-3 py-2 border ${hasAccess ? 'bg-emerald-500/10 border-emerald-400/40 text-emerald-200' : 'bg-red-500/10 border-red-400/40 text-red-200'}`} data-testid="apts-sub-status">
                  <div className="text-[10px] uppercase font-bold">{subscription.plan_name}</div>
                  <div className="text-xs">{hasAccess ? `${remainingDays}d remaining` : 'Expired — buy plan'}</div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 bg-white/5 p-1 rounded-xl overflow-x-auto" data-testid="apts-tabs">
          {[
            { id: 'dashboard', label: '📊 Dashboard' },
            { id: 'ledger', label: '💰 Ledger' },
            { id: 'accounts', label: '🏦 Accounts' },
            { id: 'receivables', label: '💸 Lena/Dena' },
            { id: 'items', label: '📦 Assets/Liab' },
            { id: 'goals', label: '🎯 Goals' },
            { id: 'notes', label: '📝 Key Points' },
            { id: 'plans', label: '🎫 Plans' },
          ].map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`apts-tab-${t.id}`} className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition ${tab === t.id ? 'bg-indigo-500 text-white' : 'text-slate-300 hover:bg-white/10'}`}>{t.label}</button>
          ))}
        </div>

        {!hasAccess && tab !== 'plans' && (
          <div className="rounded-xl border border-red-400/40 bg-red-500/10 p-4 mb-4 text-center" data-testid="apts-locked">
            <div className="font-bold text-red-200">🔒 Access Locked</div>
            <div className="text-xs text-red-300 mt-1">Aapki subscription khatam ho gayi. Koi paid plan lein taaki continue kar sakein.</div>
            <button onClick={() => setTab('plans')} className="mt-2 px-4 py-1.5 rounded-lg bg-red-500 text-white text-xs font-bold">View Plans</button>
          </div>
        )}

        {hasAccess && tab === 'dashboard' && <DashboardTab />}
        {hasAccess && tab === 'ledger' && <LedgerTab />}
        {hasAccess && tab === 'accounts' && <AccountsTab />}
        {hasAccess && tab === 'receivables' && <ReceivablesTab />}
        {hasAccess && tab === 'items' && <ItemsTab />}
        {hasAccess && tab === 'goals' && <GoalsTab />}
        {hasAccess && tab === 'notes' && <NotesTab />}
        {tab === 'plans' && <PlansTab plans={plans} subscription={subscription} onPurchased={loadSub} />}
      </div>
    </div>
  );
}

// ============ Dashboard Tab ============
const DashboardTab = () => {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get('/apts/dashboard'); setData(r.data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-center py-8 text-slate-500">Loading dashboard…</div>;

  const totalExpBreakdown = Object.entries(data.breakdown.expense || {});
  const totalIncBreakdown = Object.entries(data.breakdown.income || {});
  const expTotal = totalExpBreakdown.reduce((s, [, v]) => s + v, 0) || 1;

  return (
    <div className="space-y-3" data-testid="apts-dashboard">
      {/* Net Worth Card */}
      <div className="rounded-2xl border border-indigo-400/30 bg-gradient-to-br from-indigo-500/20 to-purple-500/10 p-4">
        <div className="text-xs uppercase text-indigo-300 font-bold">Net Worth</div>
        <div className={`text-3xl font-extrabold ${data.net_worth >= 0 ? 'text-emerald-300' : 'text-red-300'}`} data-testid="net-worth">
          ₹{data.net_worth.toLocaleString('en-IN')}
        </div>
        <div className="text-[10px] text-slate-400 mt-1">
          Assets ₹{data.total_assets.toLocaleString('en-IN')} − Liabilities ₹{data.total_liabilities.toLocaleString('en-IN')}
        </div>
      </div>

      {/* Month cards */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Income (Month)" value={data.income_month} icon={TrendingUp} tone="emerald" />
        <StatCard label="Expense (Month)" value={data.expense_month} icon={TrendingDown} tone="red" />
        <StatCard label="Savings (Month)" value={data.savings_month} icon={Wallet} tone={data.savings_month >= 0 ? 'emerald' : 'red'} />
      </div>

      {/* Receivable / Payable / Cash cards */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Lena Baki" value={data.total_receivable || 0} icon={TrendingUp} tone="emerald" />
        <StatCard label="Dena Baki" value={data.total_payable || 0} icon={TrendingDown} tone="red" />
        <StatCard label="Accounts Total" value={data.accounts_total || 0} icon={Wallet} tone="emerald" />
      </div>

      {/* Income vs Expense breakdown */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <BreakdownCard title="💚 Income by Category" data={totalIncBreakdown} tone="emerald" />
        <BreakdownCard title="💸 Expense by Category" data={totalExpBreakdown} tone="red" total={expTotal} />
      </div>

      {/* Upcoming Dues */}
      {data.upcoming_dues.length > 0 && (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-3" data-testid="upcoming-dues">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-amber-300" /><div className="font-bold text-sm">🔔 Upcoming EMI/Dues (next 7 days)</div></div>
          <div className="space-y-1">
            {data.upcoming_dues.map((d) => (
              <div key={d.item_id} className="flex items-center justify-between bg-amber-500/10 rounded-lg px-2 py-1.5 text-xs">
                <div><b>{d.name}</b> <span className="text-slate-400">· {d.type}</span></div>
                <div className="text-right">
                  {d.emi_amount > 0 && <div className="text-amber-200 font-bold">₹{d.emi_amount}</div>}
                  <div className="text-[10px] text-amber-300">{d.days_left === 0 ? 'Aaj due' : `${d.days_left}d baaki`}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Goals progress */}
      {data.goals.length > 0 && (
        <div className="rounded-2xl border border-indigo-400/30 bg-indigo-500/5 p-3">
          <div className="flex items-center gap-2 mb-2"><Target className="w-4 h-4 text-indigo-300" /><div className="font-bold text-sm">Goals Progress</div></div>
          <div className="space-y-2">
            {data.goals.map((g) => (
              <div key={g.id} className="bg-white/5 rounded-lg p-2">
                <div className="flex justify-between text-xs mb-1">
                  <b>{g.title}</b>
                  <span>₹{g.saved.toLocaleString('en-IN')} / ₹{g.target.toLocaleString('en-IN')} ({g.progress_pct}%)</span>
                </div>
                <div className="h-2 bg-slate-800 rounded overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500" style={{ width: `${Math.min(100, g.progress_pct)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, tone }) => (
  <div className={`rounded-xl border p-3 ${tone === 'emerald' ? 'border-emerald-400/30 bg-emerald-500/10' : 'border-red-400/30 bg-red-500/10'}`}>
    <div className="flex items-center gap-1 text-[10px] uppercase font-bold text-slate-400"><Icon className="w-3 h-3" />{label}</div>
    <div className={`text-lg font-extrabold ${tone === 'emerald' ? 'text-emerald-300' : 'text-red-300'}`}>₹{(value || 0).toLocaleString('en-IN')}</div>
  </div>
);

const BreakdownCard = ({ title, data, tone, total }) => {
  const sorted = [...data].sort((a, b) => b[1] - a[1]);
  const t = total || sorted.reduce((s, [, v]) => s + v, 0) || 1;
  return (
    <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3">
      <div className="text-xs font-bold mb-2">{title}</div>
      {sorted.length === 0 && <div className="text-[10px] text-slate-500">No entries this month</div>}
      {sorted.map(([cat, amt]) => (
        <div key={cat} className="mb-1.5">
          <div className="flex justify-between text-[11px]"><span className="capitalize">{cat.replace('_', ' ')}</span><span>₹{amt.toLocaleString('en-IN')}</span></div>
          <div className="h-1.5 bg-slate-800 rounded overflow-hidden mt-0.5">
            <div className={`h-full ${tone === 'emerald' ? 'bg-emerald-500' : 'bg-red-500'}`} style={{ width: `${(amt / t) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ============ Ledger Tab ============
const QUICK_ENTRY_TYPES = [
  { id: 'paise_liye',   type: 'income',  label: 'Paise Liye',  icon: '💰', cls: 'bg-emerald-500/20 border-emerald-400/60' },
  { id: 'paise_diye',   type: 'expense', label: 'Paise Diye',  icon: '💸', cls: 'bg-rose-500/20 border-rose-400/60' },
  { id: 'other_income', type: 'income',  label: 'Other Income',icon: '📈', cls: 'bg-teal-500/20 border-teal-400/60' },
  { id: 'other_expense',type: 'expense', label: 'Other Expense',icon: '🧾', cls: 'bg-orange-500/20 border-orange-400/60' },
];

const LedgerTab = () => {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ id: null, transaction_type: 'expense', category: 'paise_diye', sub_category: '', amount: '', date: new Date().toISOString().slice(0, 10), description: '', account_id: '' });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const params = filter === 'all' ? '' : `?transaction_type=${filter}`;
      const [rEntries, rAccs] = await Promise.all([
        api.get(`/apts/ledger${params}`),
        api.get('/apts/accounts'),
      ]);
      setEntries(rEntries.data.entries || []);
      setAccounts(rAccs.data.accounts || []);
    } catch { /* ignore */ }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  // Default: first active account (if any)
  const defaultAccountId = accounts[0]?.id || '';

  const quick = (q) => {
    setForm({ id: null, transaction_type: q.type, category: q.id, sub_category: '', amount: '', date: new Date().toISOString().slice(0, 10), description: '', account_id: defaultAccountId });
    setShowForm(true);
  };

  const submit = async () => {
    const a = parseFloat(form.amount);
    if (!a || a <= 0) return toast.error('Sahi amount daalein');
    // For income/expense: require an account so balance stays accurate
    if ((form.transaction_type === 'income' || form.transaction_type === 'expense') && !form.account_id) {
      if (accounts.length === 0) return toast.error('Pehle "Accounts" tab me apna Cash/Bank account banaayein');
      return toast.error('Kripya account select karein (Cash/Bank/etc.)');
    }
    setBusy(true);
    try {
      const payload = { ...form, amount: a, account_id: form.account_id || null };
      if (form.id) {
        await api.patch(`/apts/ledger/${form.id}`, payload);
        toast.success('✅ Entry updated · account balance sync');
      } else {
        await api.post('/apts/ledger', payload);
        toast.success('✅ Entry saved · account balance updated');
      }
      setForm({ id: null, transaction_type: 'expense', category: 'paise_diye', sub_category: '', amount: '', date: new Date().toISOString().slice(0, 10), description: '', account_id: defaultAccountId });
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const edit = (e) => {
    setForm({ id: e.id, transaction_type: e.transaction_type, category: e.category, sub_category: e.sub_category || '', amount: String(e.amount), date: e.date, description: e.description || '', account_id: e.account_id || '' });
    setShowForm(true);
  };
  const del = async (id) => {
    if (!window.confirm('Delete this entry? (Account balance reverse ho jayegi)')) return;
    try { await api.delete(`/apts/ledger/${id}`); toast.success('Deleted · account balance reversed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const cats = form.transaction_type === 'income' ? ['paise_liye', ...CATS_INCOME] : ['paise_diye', ...CATS_EXPENSE];

  return (
    <div className="space-y-3" data-testid="apts-ledger">
      {/* Vyapar-style Quick Entry chips */}
      <div className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-slate-900/60 to-fuchsia-950/30 p-3" data-testid="ledger-quick-chips">
        <div className="text-xs font-bold mb-2 flex items-center gap-1.5">⚡ Quick Entry</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {QUICK_ENTRY_TYPES.map((q) => (
            <button key={q.id} onClick={() => quick(q)} data-testid={`quick-${q.id}`} className={`p-2 rounded-xl border text-left transition hover:brightness-110 ${q.cls}`}>
              <div className="text-lg">{q.icon}</div>
              <div className="text-xs font-bold">{q.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex gap-1">
          {['all', 'income', 'expense', 'transfer'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-2 py-1 rounded text-[11px] font-bold capitalize ${filter === f ? 'bg-indigo-500 text-white' : 'bg-white/10 text-slate-300'}`}>{f}</button>
          ))}
        </div>
        <button onClick={() => { setForm({ id: null, transaction_type: 'expense', category: 'paise_diye', sub_category: '', amount: '', date: new Date().toISOString().slice(0, 10), description: '', account_id: defaultAccountId }); setShowForm(true); }} data-testid="ledger-add-btn" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> Add Entry</button>
      </div>

      {showForm && (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-3" data-testid="ledger-form">
          <div className="text-xs font-bold mb-2 text-emerald-300">{form.id ? '✏️ Edit Entry' : '➕ New Entry'}</div>
          {accounts.length === 0 && (form.transaction_type === 'income' || form.transaction_type === 'expense') && (
            <div className="mb-2 p-2 rounded-lg bg-amber-500/15 border border-amber-400/40 text-amber-200 text-[11px] flex items-center gap-2" data-testid="no-account-warn">
              ⚠️ <span>Pehle <b>&quot;Accounts&quot;</b> tab me apna Cash / Bank / Wallet account banaayein — tabhi balance auto-update hoga.</span>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <select value={form.transaction_type} onChange={(e) => setForm({ ...form, transaction_type: e.target.value, category: e.target.value === 'income' ? 'paise_liye' : 'paise_diye' })} data-testid="ledger-tx-type" className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm">
              <option value="income" className="bg-slate-900">Income</option>
              <option value="expense" className="bg-slate-900">Expense</option>
              <option value="transfer" className="bg-slate-900">Transfer</option>
            </select>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm capitalize">
              {cats.map((c) => <option key={c} value={c} className="bg-slate-900">{c.replace(/_/g, ' ')}</option>)}
            </select>
            <select
              value={form.account_id}
              onChange={(e) => setForm({ ...form, account_id: e.target.value })}
              data-testid="ledger-account-select"
              className="bg-white/5 border border-emerald-400/40 rounded px-2 py-1.5 text-sm font-semibold"
              title="Kaunse account se paise aaye/gaye (Cash/Bank/etc.)"
            >
              <option value="" className="bg-slate-900">
                {form.transaction_type === 'income' ? '💰 Paise kis account me?' : form.transaction_type === 'expense' ? '💸 Paise kis account se?' : '↔ Account (optional)'}
              </option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id} className="bg-slate-900">
                  {a.name} · ₹{Number(a.balance || 0).toLocaleString('en-IN')}
                </option>
              ))}
            </select>
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm" />
            <div className="relative"><span className="absolute left-2 top-1.5 text-slate-400 text-sm">₹</span><input type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="ledger-amount" className="w-full pl-5 pr-2 py-1.5 rounded bg-white/5 border border-white/10 text-sm" /></div>
          </div>
          <input placeholder="Description / Party name / Note (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full mt-2 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" />
          <div className="flex gap-2 mt-2">
            <button onClick={submit} disabled={busy} data-testid="ledger-save" className="flex-1 py-1.5 rounded bg-emerald-500 text-white text-sm font-bold disabled:opacity-60">{busy ? 'Saving…' : (form.id ? 'Update' : 'Save')}</button>
            <button onClick={() => { setShowForm(false); setForm({ ...form, id: null }); }} className="px-3 py-1.5 rounded bg-white/10 text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[620px]">
          <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Account</th><th className="text-right px-3 py-2">Amount</th><th className="px-3 py-2">Actions</th></tr></thead>
          <tbody>
            {entries.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500 text-xs">Koi entry nahi. Upar chips se quick add karein.</td></tr>}
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-3 py-2 text-xs">{e.date}</td>
                <td className="px-3 py-2"><div className="font-bold capitalize">{(e.category || '').replace(/_/g, ' ')}</div><div className="text-[10px] text-slate-400 capitalize">{e.transaction_type}{e.description ? ` · ${e.description}` : ''}</div></td>
                <td className="px-3 py-2 text-xs">{e.account_name || (e.transaction_type === 'transfer' ? (e.sub_category || '—') : <span className="text-amber-400">—</span>)}</td>
                <td className={`px-3 py-2 text-right font-bold ${e.transaction_type === 'income' ? 'text-emerald-300' : e.transaction_type === 'expense' ? 'text-red-300' : 'text-slate-300'}`}>₹{e.amount.toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-center"><div className="flex gap-1 justify-center items-center">
                  <a
                    href={(() => {
                      const isIncome = e.transaction_type === 'income';
                      const emoji = isIncome ? '🤑' : '💸';
                      const verb = isIncome ? 'kamaya' : 'kharch kiya';
                      const cat = (e.category || '').replace(/_/g, ' ');
                      const link = `${window.location.origin}/apts`;
                      const msg = `${emoji} Aaj ${cat} me ₹${Number(e.amount).toLocaleString('en-IN')} ${verb}!\n\nMain apna paisa Anant Portfolio Tracker se track karta hoon — Paise Liye/Diye · Net worth · EMI alerts sab manage.\n\n${link}`;
                      return `https://wa.me/?text=${encodeURIComponent(msg)}`;
                    })()}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Share this entry on WhatsApp"
                    data-testid={`ledger-share-${e.id}`}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 font-bold hover:bg-green-500/30"
                  >
                    📤
                  </a>
                  <button onClick={() => edit(e)} data-testid={`ledger-edit-${e.id}`} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-500/20 text-indigo-300 font-bold">Edit</button>
                  <button onClick={() => del(e.id)} className="p-1 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ Items (Assets/Liabilities) Tab ============
const ItemsTab = () => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: 'asset', name: '', category: 'savings', principal_value: '', interest_rate: '', emi_amount: '', due_date: '', frequency: 'monthly', notes: '' });
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const params = filter === 'all' ? '' : `?type=${filter}`;
      const r = await api.get(`/apts/items${params}`);
      setItems(r.data.items || []);
    } catch { /* ignore */ }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const p = parseFloat(form.principal_value);
    if (!form.name || !p || p <= 0) return toast.error('Name aur principal amount daalein');
    setBusy(true);
    try {
      await api.post('/apts/items', {
        ...form,
        principal_value: p,
        interest_rate: parseFloat(form.interest_rate) || 0,
        emi_amount: parseFloat(form.emi_amount) || 0,
      });
      toast.success('✅ Item saved');
      setForm({ type: form.type, name: '', category: form.type === 'asset' ? 'savings' : 'home_loan', principal_value: '', interest_rate: '', emi_amount: '', due_date: '', frequency: 'monthly', notes: '' });
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try { await api.delete(`/apts/items/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const cats = form.type === 'asset' ? ITEM_CATS_ASSET : ITEM_CATS_LIAB;

  return (
    <div className="space-y-3" data-testid="apts-items">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex gap-1">
          {['all', 'asset', 'liability'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-2 py-1 rounded text-[11px] font-bold capitalize ${filter === f ? 'bg-indigo-500 text-white' : 'bg-white/10 text-slate-300'}`}>{f}</button>
          ))}
        </div>
        <button onClick={() => setShowForm(true)} data-testid="items-add-btn" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white text-xs font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> Add Asset/Liability</button>
      </div>

      {showForm && (
        <div className="rounded-xl border border-indigo-400/30 bg-indigo-500/5 p-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, category: e.target.value === 'asset' ? 'savings' : 'home_loan' })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm">
              <option value="asset" className="bg-slate-900">Asset</option>
              <option value="liability" className="bg-slate-900">Liability</option>
            </select>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm capitalize">
              {cats.map((c) => <option key={c} value={c} className="bg-slate-900 capitalize">{c.replace('_', ' ')}</option>)}
            </select>
            <input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm sm:col-span-2" />
            <div className="relative"><span className="absolute left-2 top-1.5 text-slate-400 text-sm">₹</span><input type="number" placeholder="Principal Value" value={form.principal_value} onChange={(e) => setForm({ ...form, principal_value: e.target.value })} className="w-full pl-5 pr-2 py-1.5 rounded bg-white/5 border border-white/10 text-sm" /></div>
            <input type="number" placeholder="Interest %" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm" />
            <div className="relative"><span className="absolute left-2 top-1.5 text-slate-400 text-sm">₹</span><input type="number" placeholder="EMI (optional)" value={form.emi_amount} onChange={(e) => setForm({ ...form, emi_amount: e.target.value })} className="w-full pl-5 pr-2 py-1.5 rounded bg-white/5 border border-white/10 text-sm" /></div>
            <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} placeholder="Next due" className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm" />
          </div>
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full mt-2 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" />
          <div className="flex gap-2 mt-2">
            <button onClick={submit} disabled={busy} data-testid="items-save" className="flex-1 py-1.5 rounded bg-indigo-500 text-white text-sm font-bold disabled:opacity-60">{busy ? 'Saving…' : 'Save'}</button>
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded bg-white/10 text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {items.length === 0 && <div className="col-span-2 text-center py-6 text-slate-500 text-xs">Koi asset/liability nahi hai</div>}
        {items.map((i) => (
          <div key={i.id} className={`rounded-xl border p-3 ${i.type === 'asset' ? 'border-emerald-400/30 bg-emerald-500/5' : 'border-red-400/30 bg-red-500/5'}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-400">{i.type} · {(i.category || '').replace('_', ' ')}</div>
                <div className="font-bold">{i.name}</div>
              </div>
              <button onClick={() => del(i.id)} className="p-1 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className={`text-xl font-extrabold mt-1 ${i.type === 'asset' ? 'text-emerald-300' : 'text-red-300'}`}>₹{i.principal_value.toLocaleString('en-IN')}</div>
            <div className="text-[10px] text-slate-400 mt-1">
              {i.interest_rate > 0 && <span>@ {i.interest_rate}% · </span>}
              {i.emi_amount > 0 && <span>EMI ₹{i.emi_amount} · </span>}
              {i.due_date && <span>Due {i.due_date}</span>}
            </div>
            {i.notes && <div className="text-[10px] text-slate-500 mt-1 italic">{i.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ Goals Tab ============
const GoalsTab = () => {
  const [goals, setGoals] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', target_amount: '', saved_amount: '', target_date: '', frequency: 'yearly' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/apts/goals'); setGoals(r.data.goals || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const t = parseFloat(form.target_amount);
    if (!form.title || !t || t <= 0) return toast.error('Title aur target amount daalein');
    setBusy(true);
    try {
      await api.post('/apts/goals', { ...form, target_amount: t, saved_amount: parseFloat(form.saved_amount) || 0 });
      toast.success('✅ Goal saved');
      setForm({ title: '', target_amount: '', saved_amount: '', target_date: '', frequency: 'yearly' });
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const updateSaved = async (g, amount) => {
    try { await api.patch(`/apts/goals/${g.id}`, { ...g, saved_amount: amount }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try { await api.delete(`/apts/goals/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="apts-goals">
      <div className="flex justify-end"><button onClick={() => setShowForm(true)} data-testid="goals-add-btn" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white text-xs font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> Add Goal</button></div>
      {showForm && (
        <div className="rounded-xl border border-fuchsia-400/30 bg-fuchsia-500/5 p-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <input placeholder="Goal title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm sm:col-span-2" />
            <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm">
              <option value="monthly" className="bg-slate-900">Monthly</option>
              <option value="yearly" className="bg-slate-900">Yearly</option>
              <option value="one_time" className="bg-slate-900">One Time</option>
            </select>
            <input type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm" />
            <div className="relative"><span className="absolute left-2 top-1.5 text-slate-400 text-sm">₹</span><input type="number" placeholder="Target Amount" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} className="w-full pl-5 pr-2 py-1.5 rounded bg-white/5 border border-white/10 text-sm" /></div>
            <div className="relative"><span className="absolute left-2 top-1.5 text-slate-400 text-sm">₹</span><input type="number" placeholder="Already Saved" value={form.saved_amount} onChange={(e) => setForm({ ...form, saved_amount: e.target.value })} className="w-full pl-5 pr-2 py-1.5 rounded bg-white/5 border border-white/10 text-sm" /></div>
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={submit} disabled={busy} data-testid="goals-save" className="flex-1 py-1.5 rounded bg-fuchsia-500 text-white text-sm font-bold disabled:opacity-60">{busy ? 'Saving…' : 'Save'}</button>
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded bg-white/10 text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {goals.length === 0 && <div className="col-span-2 text-center py-6 text-slate-500 text-xs">Koi goal nahi hai</div>}
        {goals.map((g) => {
          const pct = g.target_amount > 0 ? Math.min(100, (g.saved_amount || 0) / g.target_amount * 100) : 0;
          return (
            <div key={g.id} className="rounded-xl border border-indigo-400/30 bg-indigo-500/5 p-3">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-bold">{g.title}</div>
                  <div className="text-[10px] text-slate-400 capitalize">{g.frequency}{g.target_date ? ` · by ${g.target_date}` : ''}</div>
                </div>
                <button onClick={() => del(g.id)} className="p-1 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="mt-2 text-xs">₹{(g.saved_amount || 0).toLocaleString('en-IN')} / ₹{g.target_amount.toLocaleString('en-IN')} ({pct.toFixed(1)}%)</div>
              <div className="h-2 bg-slate-800 rounded overflow-hidden mt-1"><div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500" style={{ width: `${pct}%` }} /></div>
              <div className="flex gap-1 mt-2">
                <button onClick={() => updateSaved(g, (g.saved_amount || 0) + 100)} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">+₹100</button>
                <button onClick={() => updateSaved(g, (g.saved_amount || 0) + 1000)} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">+₹1000</button>
                <button onClick={() => { const v = parseFloat(prompt('Add amount to saved:', '500')); if (v > 0) updateSaved(g, (g.saved_amount || 0) + v); }} className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-slate-300 font-bold">+Custom</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============ Accounts Tab ============
const ACCOUNT_TYPES = [
  { id: 'savings', label: 'Savings', icon: '🏦' },
  { id: 'current', label: 'Current', icon: '💼' },
  { id: 'wallet', label: 'Wallet', icon: '📱' },
  { id: 'cash', label: 'Cash', icon: '💵' },
  { id: 'credit_card', label: 'Credit Card', icon: '💳' },
  { id: 'other', label: 'Other', icon: '📌' },
];

const AccountsTab = () => {
  const [accs, setAccs] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', account_type: 'savings', balance: '', notes: '' });
  const [xfer, setXfer] = useState({ open: false, from: '', to: '', amount: '', note: '', busy: false });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/apts/accounts'); setAccs(r.data.accounts || []); setTotal(r.data.total_balance || 0); }
    catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.name) return toast.error('Account name daalein');
    setBusy(true);
    try {
      await api.post('/apts/accounts', { ...form, balance: parseFloat(form.balance) || 0 });
      toast.success('✅ Account added');
      setForm({ name: '', account_type: 'savings', balance: '', notes: '' });
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete account? (Balance history preserved)')) return;
    try { await api.delete(`/apts/accounts/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const submitTransfer = async () => {
    if (!xfer.from || !xfer.to) return toast.error('Both accounts select karein');
    if (xfer.from === xfer.to) return toast.error('Same account nahi ho sakta');
    const amt = parseFloat(xfer.amount);
    if (!amt || amt <= 0) return toast.error('Sahi amount daalein');
    setXfer((x) => ({ ...x, busy: true }));
    try {
      const r = await api.post('/apts/accounts/transfer', { from_account_id: xfer.from, to_account_id: xfer.to, amount: amt, note: xfer.note });
      toast.success(`✅ Transferred ₹${amt}. New balances: ₹${r.data.from_new_balance} → ₹${r.data.to_new_balance}`);
      setXfer({ open: false, from: '', to: '', amount: '', note: '', busy: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); setXfer((x) => ({ ...x, busy: false })); }
  };

  return (
    <div className="space-y-3" data-testid="apts-accounts">
      <div className="rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/15 to-teal-500/5 p-4 flex items-center justify-between">
        <div><div className="text-xs uppercase text-emerald-300 font-bold">Total Balance (All Accounts)</div><div className="text-3xl font-extrabold text-emerald-200">₹{total.toLocaleString('en-IN')}</div></div>
        <div className="flex flex-col gap-2">
          <button onClick={() => setShowForm(true)} data-testid="acc-add-btn" className="px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> Add Account</button>
          <button onClick={() => setXfer({ ...xfer, open: true })} disabled={accs.length < 2} data-testid="acc-transfer-btn" className="px-3 py-1.5 rounded-lg bg-indigo-500 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-40">🔄 Transfer</button>
        </div>
      </div>

      {showForm && (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <input placeholder="Name (SBI, HDFC…)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm sm:col-span-2" />
            <select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm">
              {ACCOUNT_TYPES.map((t) => <option key={t.id} value={t.id} className="bg-slate-900">{t.icon} {t.label}</option>)}
            </select>
            <div className="relative"><span className="absolute left-2 top-1.5 text-slate-400 text-sm">₹</span><input type="number" placeholder="Opening Balance" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} className="w-full pl-5 pr-2 py-1.5 rounded bg-white/5 border border-white/10 text-sm" /></div>
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={submit} disabled={busy} data-testid="acc-save" className="flex-1 py-1.5 rounded bg-emerald-500 text-white text-sm font-bold disabled:opacity-60">{busy ? 'Saving…' : 'Save'}</button>
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded bg-white/10 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {xfer.open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !xfer.busy && setXfer({ ...xfer, open: false })}>
          <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-indigo-400/40 p-5" onClick={(e) => e.stopPropagation()} data-testid="xfer-modal">
            <div className="flex justify-between items-center mb-3"><h3 className="text-lg font-extrabold">🔄 Transfer Between Accounts</h3><button onClick={() => setXfer({ ...xfer, open: false })}><X className="w-4 h-4" /></button></div>
            <label className="text-[11px] uppercase font-bold text-slate-400">From Account</label>
            <select value={xfer.from} onChange={(e) => setXfer({ ...xfer, from: e.target.value })} data-testid="xfer-from" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
              <option value="" className="bg-slate-900">-- select --</option>
              {accs.map((a) => <option key={a.id} value={a.id} className="bg-slate-900">{a.name} (₹{a.balance})</option>)}
            </select>
            <label className="text-[11px] uppercase font-bold text-slate-400 mt-3 block">To Account</label>
            <select value={xfer.to} onChange={(e) => setXfer({ ...xfer, to: e.target.value })} data-testid="xfer-to" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm">
              <option value="" className="bg-slate-900">-- select --</option>
              {accs.filter((a) => a.id !== xfer.from).map((a) => <option key={a.id} value={a.id} className="bg-slate-900">{a.name} (₹{a.balance})</option>)}
            </select>
            <label className="text-[11px] uppercase font-bold text-slate-400 mt-3 block">Amount</label>
            <div className="relative"><span className="absolute left-2.5 top-2 text-slate-400 text-sm">₹</span><input type="number" placeholder="0" value={xfer.amount} onChange={(e) => setXfer({ ...xfer, amount: e.target.value })} data-testid="xfer-amount" className="w-full pl-6 pr-2 py-2 rounded-lg bg-white/5 border border-white/10 text-sm" /></div>
            <input placeholder="Note (optional)" value={xfer.note} onChange={(e) => setXfer({ ...xfer, note: e.target.value })} className="w-full mt-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs" />
            <button onClick={submitTransfer} disabled={xfer.busy} data-testid="xfer-submit" className="w-full mt-4 py-2.5 rounded-lg bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white font-bold text-sm disabled:opacity-60">{xfer.busy ? 'Transferring…' : 'Transfer Now'}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {accs.length === 0 && <div className="col-span-2 text-center py-6 text-slate-500 text-xs">Koi account nahi. Upar se add karein.</div>}
        {accs.map((a) => {
          const meta = ACCOUNT_TYPES.find((t) => t.id === a.account_type) || ACCOUNT_TYPES[0];
          return (
            <div key={a.id} className="rounded-xl border border-white/10 bg-white/5 p-3">
              <div className="flex justify-between items-start">
                <div><div className="text-[10px] uppercase font-bold text-slate-400">{meta.icon} {meta.label}</div><div className="font-bold">{a.name}</div></div>
                <button onClick={() => del(a.id)} className="p-1 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className={`text-2xl font-extrabold mt-1 ${a.balance >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>₹{a.balance.toLocaleString('en-IN')}</div>
              {a.notes && <div className="text-[10px] text-slate-500 mt-1">{a.notes}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============ Receivables/Payables Tab ============
const ReceivablesTab = () => {
  const [data, setData] = useState({ items: [], total_receivable: 0, total_payable: 0, net: 0 });
  const [accs, setAccs] = useState([]);
  const [form, setForm] = useState({ type: 'receivable', party_name: '', party_contact: '', amount: '', due_date: '', note: '' });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([
        api.get(`/apts/receivables${filter !== 'all' ? `?type=${filter}` : ''}`),
        api.get('/apts/accounts'),
      ]);
      setData(r.data); setAccs(a.data.accounts || []);
    } catch { /* ignore */ }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const a = parseFloat(form.amount);
    if (!form.party_name || !a || a <= 0) return toast.error('Party name aur amount daalein');
    setBusy(true);
    try {
      await api.post('/apts/receivables', { ...form, amount: a });
      toast.success('✅ Entry added');
      setForm({ type: form.type, party_name: '', party_contact: '', amount: '', due_date: '', note: '' });
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const settle = async (item) => {
    const amt = parseFloat(prompt(`Settle amount for ${item.party_name} (Remaining ₹${item.remaining})`, item.remaining));
    if (!amt || amt <= 0) return;
    let acc_id = null;
    if (accs.length > 0 && window.confirm('Kis account se paise move karein? OK=Select | Cancel=Just mark settled')) {
      const names = accs.map((a, i) => `${i + 1}. ${a.name} (₹${a.balance})`).join('\n');
      const pick = parseInt(prompt(`Choose account:\n${names}`, '1'));
      if (pick >= 1 && pick <= accs.length) acc_id = accs[pick - 1].id;
    }
    try {
      const r = await api.post(`/apts/receivables/${item.id}/settle`, { amount: amt, account_id: acc_id });
      toast.success(`✅ Settled ₹${amt}. Remaining ₹${r.data.remaining} · ${r.data.status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await api.delete(`/apts/receivables/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="apts-receivables">
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3"><div className="text-[10px] uppercase font-bold text-emerald-300">Lena Baki</div><div className="text-xl font-extrabold text-emerald-200">₹{data.total_receivable.toLocaleString('en-IN')}</div></div>
        <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-3"><div className="text-[10px] uppercase font-bold text-red-300">Dena Baki</div><div className="text-xl font-extrabold text-red-200">₹{data.total_payable.toLocaleString('en-IN')}</div></div>
        <div className={`rounded-xl border p-3 ${data.net >= 0 ? 'border-indigo-400/30 bg-indigo-500/10' : 'border-orange-400/30 bg-orange-500/10'}`}><div className="text-[10px] uppercase font-bold">Net</div><div className={`text-xl font-extrabold ${data.net >= 0 ? 'text-indigo-200' : 'text-orange-200'}`}>₹{data.net.toLocaleString('en-IN')}</div></div>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="flex gap-1">
          {['all', 'receivable', 'payable'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-2 py-1 rounded text-[11px] font-bold capitalize ${filter === f ? 'bg-indigo-500 text-white' : 'bg-white/10 text-slate-300'}`}>{f}</button>
          ))}
        </div>
        <button onClick={() => setShowForm(true)} data-testid="rec-add-btn" className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-red-500 text-white text-xs font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
      </div>

      {showForm && (
        <div className="rounded-xl border border-indigo-400/30 bg-indigo-500/5 p-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm">
              <option value="receivable" className="bg-slate-900">💚 Receivable (Lena)</option>
              <option value="payable" className="bg-slate-900">🔴 Payable (Dena)</option>
            </select>
            <input placeholder="Party name *" value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm sm:col-span-2" />
            <input placeholder="Mobile" value={form.party_contact} onChange={(e) => setForm({ ...form, party_contact: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm" />
            <div className="relative"><span className="absolute left-2 top-1.5 text-slate-400 text-sm">₹</span><input type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full pl-5 pr-2 py-1.5 rounded bg-white/5 border border-white/10 text-sm" /></div>
            <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm" />
            <input placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm sm:col-span-2" />
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={submit} disabled={busy} data-testid="rec-save" className="flex-1 py-1.5 rounded bg-indigo-500 text-white text-sm font-bold disabled:opacity-60">{busy ? 'Saving…' : 'Save'}</button>
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded bg-white/10 text-sm">Cancel</button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-white/5 text-xs uppercase text-slate-400"><tr><th className="text-left px-3 py-2">Party</th><th className="text-right px-3 py-2">Amount</th><th className="text-right px-3 py-2">Remaining</th><th className="px-3 py-2">Due</th><th className="px-3 py-2">Status</th><th></th></tr></thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500 text-xs">Koi entry nahi</td></tr>}
            {data.items.map((i) => (
              <tr key={i.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-3 py-2"><div className="flex items-center gap-2"><span className={`text-lg ${i.type === 'receivable' ? '💚' : '🔴'}`}>{i.type === 'receivable' ? '💚' : '🔴'}</span><div><div className="font-bold">{i.party_name}</div><div className="text-[10px] text-slate-400">{i.party_contact || '—'}</div></div></div></td>
                <td className="px-3 py-2 text-right font-bold">₹{i.amount.toLocaleString('en-IN')}</td>
                <td className={`px-3 py-2 text-right font-bold ${i.type === 'receivable' ? 'text-emerald-300' : 'text-red-300'}`}>₹{(i.remaining || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-center text-xs">{i.due_date || '—'}</td>
                <td className="px-3 py-2 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full ${i.status === 'settled' ? 'bg-emerald-500/20 text-emerald-300' : i.status === 'partial' ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-500/20 text-slate-400'}`}>{i.status}</span></td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-center">
                    {i.status !== 'settled' && <button onClick={() => settle(i)} data-testid={`rec-settle-${i.id}`} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500 text-white font-bold">Settle</button>}
                    <button onClick={() => del(i.id)} className="p-1 text-red-300"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============ Notes / Key Points Tab ============
const NotesTab = () => {
  const [notes, setNotes] = useState([]);
  const [form, setForm] = useState({ id: null, title: '', content: '', pinned: false, tags: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/apts/notes'); setNotes(r.data.notes || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim()) return toast.error('Title daalein');
    setBusy(true);
    try {
      const payload = { title: form.title, content: form.content, pinned: form.pinned, tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean) };
      if (form.id) await api.patch(`/apts/notes/${form.id}`, payload);
      else await api.post('/apts/notes', payload);
      toast.success('✅ Saved');
      setForm({ id: null, title: '', content: '', pinned: false, tags: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const edit = (n) => setForm({ id: n.id, title: n.title, content: n.content, pinned: n.pinned, tags: (n.tags || []).join(', ') });
  const del = async (id) => {
    if (!window.confirm('Delete note?')) return;
    try { await api.delete(`/apts/notes/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="apts-notes">
      <div className="rounded-xl border border-yellow-400/30 bg-yellow-500/5 p-3">
        <div className="text-xs uppercase font-bold text-yellow-300 mb-2">📝 {form.id ? 'Edit Key Point' : 'Add Key Point'}</div>
        <input placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="note-title" className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm mb-2" />
        <textarea placeholder="Content / body" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="note-content" rows={4} className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm mb-2" />
        <div className="grid grid-cols-2 gap-2">
          <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs" />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} data-testid="note-pin" /> 📌 Pin to top</label>
        </div>
        <div className="flex gap-2 mt-2">
          <button onClick={submit} disabled={busy} data-testid="note-save" className="flex-1 py-1.5 rounded bg-yellow-500 text-slate-900 text-sm font-bold disabled:opacity-60">{busy ? 'Saving…' : (form.id ? 'Update' : 'Save')}</button>
          {form.id && <button onClick={() => setForm({ id: null, title: '', content: '', pinned: false, tags: '' })} className="px-3 py-1.5 rounded bg-white/10 text-sm">Cancel</button>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {notes.length === 0 && <div className="col-span-2 text-center py-6 text-slate-500 text-xs">Koi note nahi</div>}
        {notes.map((n) => (
          <div key={n.id} className={`rounded-xl border p-3 ${n.pinned ? 'border-yellow-400/40 bg-yellow-500/10' : 'border-white/10 bg-white/5'}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="min-w-0"><div className="font-bold truncate">{n.pinned ? '📌 ' : ''}{n.title}</div><div className="text-[10px] text-slate-400">{new Date(n.updated_at).toLocaleString('en-IN')}</div></div>
              <div className="flex gap-1">
                <button onClick={() => edit(n)} className="p-1 text-indigo-300 text-xs font-bold">Edit</button>
                <button onClick={() => del(n.id)} className="p-1 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {n.content && <div className="text-xs text-slate-300 mt-2 whitespace-pre-wrap">{n.content}</div>}
            {n.tags && n.tags.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{n.tags.map((t, i) => <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-slate-300">#{t}</span>)}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ Plans Tab ============
const PlansTab = ({ plans, subscription, onPurchased }) => {
  const [busy, setBusy] = useState(null);
  const buy = async (planId) => {
    if (!window.confirm('AT wallet se plan lene wale hain. Confirm?')) return;
    setBusy(planId);
    try {
      const r = await api.post('/apts/subscribe', { plan_id: planId });
      toast.success(`✅ ${r.data.subscription.plan_name} activated!`);
      onPurchased();
    } catch (e) { toast.error(e.response?.data?.detail || 'Purchase failed'); }
    finally { setBusy(null); }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="apts-plans">
      {plans.filter((p) => !p.trial).map((p) => (
        <div key={p.id} className={`rounded-2xl border p-4 ${subscription?.plan_id === p.id && subscription?.status === 'active' ? 'border-emerald-400/60 bg-emerald-500/10' : 'border-white/10 bg-white/5'}`} data-testid={`plan-${p.id}`}>
          <div className="text-xs uppercase text-slate-400 font-bold">{p.name}</div>
          <div className="text-3xl font-extrabold mt-1">{p.price_at} <span className="text-sm">AT</span></div>
          <div className="text-[10px] text-slate-400">{p.duration_days} days</div>
          <button onClick={() => buy(p.id)} disabled={busy === p.id} data-testid={`buy-${p.id}`} className="w-full mt-3 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white text-sm font-bold disabled:opacity-60">
            {busy === p.id ? 'Processing…' : (subscription?.plan_id === p.id && subscription?.status === 'active' ? 'Current' : 'Subscribe')}
          </button>
        </div>
      ))}
    </div>
  );
};
